// What every quick task input reads out of a typed task, the same in every language because
// it only takes symbols: "#project", "@person" (or "@bot"), "/list" of the project and
// "+label" (a whole name, spaces and all, once picked from the suggestions: "#Lumen Co -
// Marketing Site", "@Jana Nová"; or the start of a single one: "#kos"), "p1".."p4" (or
// "!1".."!4", "!p1"), durations like "30m", "2h", "1h30m", "~30m". Everything else stays the
// title; a "/word" or "+word" that names no list or label stays in it too ("and /or", "C++"). Words that merely contain these stay as they are:
// only whole, space-separated tokens count.

const PRIORITY = /^(?:!?p|!)([1-4])$/i;
const DURATION = /^(?:(\d{1,2})h)?(?:(\d{1,3})(?:m|min)?)?$/i;
const HOURS_ONLY = /^(\d{1,2})h$/i;
const MINUTES_ONLY = /^(\d{1,3})(?:m|min)$/i;

const fold = (s) => String(s || '').toLocaleLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '');

/** What a "#token" / "@token" names: exact name first, then without spaces, then the one starting so. */
function matchNamed(token, items, nameOf) {
  const want = fold(token);
  if (!want) return null;
  const named = items.filter((x) => x && nameOf(x));
  return named.find((x) => fold(nameOf(x)) === want)
    || (() => {
      const starts = named.filter((x) => fold(nameOf(x)).startsWith(want));
      return starts.length === 1 ? starts[0] : null;
    })();
}
export const matchProject = (token, projects = []) => matchNamed(token, projects, (p) => p.name);
export const personName = (m) => m.full_name || m.name || m.email || '';
export const matchPerson = (token, people = []) => matchNamed(token, people, personName);
const nameOf = (x) => x.name || '';

// "#" or "@" followed by a whole name (spaces and all) of one of these, then a space or the end:
// the longest name wins ("#Lumen Co" vs "#Lumen Co - Site"). Names are compared ignoring case.
function wholeNames(text, sign, items, nameOf) {
  const sorted = items.filter((x) => x && nameOf(x)).sort((a, b) => nameOf(b).length - nameOf(a).length);
  const found = [];
  let rest = text;
  for (let at = rest.indexOf(sign); at !== -1; at = rest.indexOf(sign, at + 1)) {
    if (at > 0 && !/\s/.test(rest[at - 1])) continue;
    const after = rest.slice(at + 1).toLocaleLowerCase();
    const hit = sorted.find((x) => {
      const name = nameOf(x).toLocaleLowerCase();
      return name.includes(' ') && after.startsWith(name) && (after.length === name.length || /\s/.test(after[name.length]));
    });
    if (hit) {
      found.push(hit);
      rest = `${rest.slice(0, at)}${rest.slice(at + 1 + nameOf(hit).length)}`;
      at -= 1;
    }
  }
  return { found, rest };
}

function readDuration(token) {
  if (MINUTES_ONLY.test(token)) return Number(token.match(MINUTES_ONLY)[1]);
  if (HOURS_ONLY.test(token)) return Number(token.match(HOURS_ONLY)[1]) * 60;
  const m = token.match(DURATION);
  if (m && m[1] && m[2]) return Number(m[1]) * 60 + Number(m[2]);
  return null;
}

/** The "#Whole Project Name" and "@Whole Person Name" in a text (names with spaces), and the
 * text without them: read first, so no word of a name is taken for a day or a time. */
export function takeNames(text, { projects = [], people = [], sections = [], labels = [] } = {}) {
  const byProject = wholeNames(String(text || ''), '#', projects, nameOf);
  const byPerson = wholeNames(byProject.rest, '@', people, personName);
  const bySection = wholeNames(byPerson.rest, '/', sections, nameOf);
  const byLabel = wholeNames(bySection.rest, '+', labels, nameOf);
  return { project: byProject.found[0] || null, person: byPerson.found[0] || null,
    section: bySection.found[0] || null, labels: byLabel.found, rest: byLabel.rest };
}

/**
 * Split a typed task into its title and what the symbols say. `people`: who the task can be
 * given to (the target project's team and the bots that may work on it, `bot_id`);
 * `sections`: the target project's lists; `labels`: the person's labels.
 * Returns { title, project, projectToken, person, personToken, section, labels, priority,
 * duration } (null / [] where not given).
 */
export function parseTaskText(text, { projects = [], people = [], sections = [], labels = [] } = {}) {
  const out = { title: '', project: null, projectToken: null, person: null, personToken: null,
    section: null, labels: [], priority: null, duration: null };
  const kept = [];
  // Whole names with spaces first (what the suggestions put in), then single tokens.
  const named = takeNames(text, { projects, people, sections, labels });
  Object.assign(out, { project: named.project, projectToken: named.project?.name || null,
    person: named.person, personToken: named.person ? personName(named.person) : null,
    section: named.section, labels: [...named.labels] });
  named.rest.split(/\s+/).filter(Boolean).forEach((word) => {
    if (word.length > 1 && word.startsWith('/') && !out.section) {
      const section = matchNamed(word.slice(1), sections, nameOf);
      if (section) { out.section = section; return; }
    }
    if (word.length > 1 && word.startsWith('+')) {
      const label = matchNamed(word.slice(1), labels, nameOf);
      if (label && !out.labels.includes(label)) { out.labels.push(label); return; }
    }
    if (word.length > 1 && word.startsWith('#') && !out.projectToken) {
      out.projectToken = word.slice(1);
      out.project = matchProject(out.projectToken, projects);
      return;
    }
    if (word.length > 1 && word.startsWith('@') && !out.personToken) {
      out.personToken = word.slice(1);
      out.person = matchPerson(out.personToken, people);
      return;
    }
    const prio = word.match(PRIORITY);
    if (prio && !out.priority) {
      out.priority = `p${prio[1]}`;
      return;
    }
    const minutes = /\d/.test(word) && /[hm]/i.test(word) ? readDuration(word.replace(/^~/, '')) : null;
    if (minutes && minutes > 0 && minutes <= 24 * 60 && !out.duration) {
      out.duration = minutes;
      return;
    }
    kept.push(word);
  });
  out.title = kept.join(' ').trim();
  return out;
}

/** "1 h 30 min" style label for a number of minutes (units from the caller's language). */
export function durationLabel(minutes, { h = 'h', min = 'min' } = {}) {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} ${min}`;
  return rest ? `${hours} ${h} ${rest} ${min}` : `${hours} ${h}`;
}
